import React, { useState, useEffect } from "react";
import BaseTailoringQuestions from "../shared/BaseTailoringQuestions";
import type { TailoringQuestion } from "../shared/BaseTailoringQuestions";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { AlertCircle, FileText, FileSignature } from "lucide-react";
import internalAuditData from "../../constants/ConcludingProcedures/InternalAudit.json";
import PlanningSignoffDialog, { PlanningSignoffTokens } from "../planning/PlanningSignoffDialog";
import { useAuth } from "@/contexts/AuthContext";
import { loadMiscSignoffs, getSignoffEntriesForCard, type MiscSignoffEntry } from "@/utils/misc-signoff-utils";
import { useEngagement } from "@/contexts/EngagementContext";
import { apiService } from "@/services/api-service";

interface InternalAuditLandingProps {
  onSelect?: (section: string) => void;
}

// Transform JSON structure to TailoringQuestion format
function transformJSONToQuestions(jsonData: any): TailoringQuestion[] {
  const questions: TailoringQuestion[] = [];
  const sections = jsonData.sections || [];

  // Helper to extract guidance from parentheses in text
  const extractGuidance = (text: string): { mainText: string; guidance?: TailoringQuestion["guidance"] } => {
    // Find text in parentheses - handle nested parentheses
    const openParenIndex = text.indexOf('(');
    if (openParenIndex === -1) {
      return { mainText: text };
    }

    let depth = 0;
    let closeParenIndex = -1;
    
    for (let i = openParenIndex; i < text.length; i++) {
      if (text[i] === '(') {
        depth++;
      } else if (text[i] === ')') {
        depth--;
        if (depth === 0) {
          closeParenIndex = i;
          break;
        }
      }
    }

    if (closeParenIndex === -1) {
      return { mainText: text };
    }

    const mainText = text.substring(0, openParenIndex).trim();
    const guidanceText = text.substring(openParenIndex + 1, closeParenIndex).trim();

    // Parse guidance text into items
    const items: string[] = [];
    
    // Split by double newlines first to get major sections
    const sections = guidanceText.split(/\n\n+/).map(s => s.trim()).filter(s => s.length > 0);
    
    for (const section of sections) {
      // Check if section starts with a pattern like "a.", "b.", "i.", "ii.", etc.
      const itemMatch = section.match(/^([a-z]|[ivx]+)\.\s*(.+)$/is);
      if (itemMatch) {
        // This is a numbered item
        items.push(section.trim());
      } else if (section.startsWith('-')) {
        // Bullet point
        items.push(section.substring(1).trim());
      } else {
        // Check if it contains sub-items (like "a. The more judgment...")
        const lines = section.split('\n').map(l => l.trim()).filter(l => l.length > 0);
        let currentItem = '';
        
        for (const line of lines) {
          const subItemMatch = line.match(/^([a-z]|[ivx]+)\.\s*(.+)$/i);
          if (subItemMatch) {
            if (currentItem) {
              items.push(currentItem.trim());
            }
            currentItem = line;
          } else if (line.startsWith('-')) {
            if (currentItem) {
              items.push(currentItem.trim());
            }
            items.push(line.substring(1).trim());
            currentItem = '';
          } else {
            if (currentItem) {
              currentItem += ' ' + line;
            } else {
              currentItem = line;
            }
          }
        }
        if (currentItem) {
          items.push(currentItem.trim());
        }
      }
    }

    // If no items found, use the whole guidance text as a single item
    if (items.length === 0 && guidanceText) {
      items.push(guidanceText);
    }

    return {
      mainText,
      guidance: items.length > 0 ? {
        text: "",
        items
      } : undefined
    };
  };

  // Helper to create a question
  const createQuestion = (
    id: string,
    questionText: string,
    type: "radio" | "text" | "table" | "dropdown" | "textarea",
    options?: string[],
    followUp?: TailoringQuestion["follow_up"],
    tableConfig?: TailoringQuestion["tableConfig"],
    subItems?: TailoringQuestion["subItems"],
    hasDocumentManager?: boolean,
    guidance?: TailoringQuestion["guidance"]
  ): TailoringQuestion => {
    return {
      id,
      question: questionText,
      type,
      options,
      follow_up: followUp,
      tableConfig,
      subItems,
      hasDocumentManager,
      guidance,
    };
  };

  sections.forEach((section: any, sectionIndex: number) => {
    const questionIdPrefix = `ITAUD-${sectionIndex + 1}`;
    
    // Handle question 1: Radio with Yes/No and DocumentManager
    if (section.number === "1" && section.text && section.type === "radio") {
      let hasDocumentManager = false;
      if (section.items && Array.isArray(section.items)) {
        hasDocumentManager = section.items.some((item: any) => item.text === "[Document]");
      }
      
      const question = createQuestion(
        questionIdPrefix,
        section.text,
        "radio",
        section.options || ["Yes", "No"]
      );
      
      // If there's a [Document] marker, add a follow-up with DocumentManager
      if (hasDocumentManager) {
        question.follow_up = {
          id: `${questionIdPrefix}-doc`,
          question: "Document:",
          type: "document",
          condition: "always" // Always show the document manager
        };
      }
      
      questions.push(question);
    }
    // Handle conditional section (only shows if Q1 is Yes)
    else if (section.condition && section.condition === "Yes" && section.items) {
      section.items.forEach((item: any, itemIndex: number) => {
        const itemId = `ITAUD-${item.number || itemIndex + 2}`;
        
        if (item.number && item.text && item.type === "radio" && item.options) {
          // Create the main question
          const question = createQuestion(
            itemId,
            `${item.number}. ${item.text}`,
            "radio",
            item.options
          );
          
          // Add condition to show only if Q1 is "Yes"
          question.condition = {
            questionId: "ITAUD-1",
            value: "Yes"
          };
          
          questions.push(question);
          
          // Process follow-up items if they exist
          if (item.followUp && item.followUp.items) {
            item.followUp.items.forEach((followUpItem: any, followUpIndex: number) => {
              if (followUpItem.text === "[Document]") {
                // Skip [Document] markers - they're handled by the previous item
                return;
              }
              
              if (followUpItem.number) {
                // Item with roman numeral - create a separate question
                // Check if next item is [Document] to attach DocumentManager
                const nextItem = item.followUp.items[followUpIndex + 1];
                const hasDocument = nextItem && nextItem.text === "[Document]";
                
                // Extract guidance from parentheses
                const { mainText, guidance } = extractGuidance(followUpItem.text);
                
                // Use "text" type instead of "textarea" if it needs DocumentManager
                // BaseTailoringQuestions only shows DocumentManager for "text" or "document" types
                const questionType = followUpItem.type === "radio" 
                  ? "radio" 
                  : (hasDocument ? "text" : "textarea");
                
                const followUpQuestion = createQuestion(
                  `${itemId}-${followUpItem.number}`,
                  `${followUpItem.number}. ${mainText}`,
                  questionType,
                  followUpItem.options,
                  undefined,
                  undefined,
                  undefined,
                  undefined,
                  guidance
                );
                followUpQuestion.condition = {
                  questionId: itemId,
                  value: item.followUp.condition || "Yes"
                };
                questions.push(followUpQuestion);
                
                // Handle nested follow-up (like question 3 item v)
                if (followUpItem.followUp && followUpItem.followUp.items) {
                  followUpItem.followUp.items.forEach((nestedItem: any, nestedIndex: number) => {
                    if (nestedItem.text === "[Document]") {
                      // Create DocumentManager question for nested follow-up
                      const docQuestion = createQuestion(
                        `${itemId}-${followUpItem.number}-doc`,
                        "Document:",
                        "text", // Use "text" type so DocumentManager shows
                        undefined
                      );
                      docQuestion.condition = {
                        questionId: `${itemId}-${followUpItem.number}`,
                        value: followUpItem.followUp.condition || "Yes"
                      };
                      questions.push(docQuestion);
                    } else {
                      // Check if next nested item is [Document]
                      const nextNestedItem = followUpItem.followUp.items[nestedIndex + 1];
                      const hasNestedDocument = nextNestedItem && nextNestedItem.text === "[Document]";
                      
                      // Extract guidance from parentheses
                      const { mainText, guidance } = extractGuidance(nestedItem.text);
                      
                      const nestedQuestionType = nestedItem.type === "radio" 
                        ? "radio" 
                        : (hasNestedDocument ? "text" : "textarea");
                      
                      const nestedQuestion = createQuestion(
                        `${itemId}-${followUpItem.number}-item-${nestedIndex}`,
                        mainText,
                        nestedQuestionType,
                        nestedItem.options,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        guidance
                      );
                      nestedQuestion.condition = {
                        questionId: `${itemId}-${followUpItem.number}`,
                        value: followUpItem.followUp.condition || "Yes"
                      };
                      questions.push(nestedQuestion);
                    }
                  });
                }
              }
            });
          }
        } else if (item.number && item.text && item.type === "radio" && item.options) {
          // Question without follow-up
          const question = createQuestion(
            itemId,
            `${item.number}. ${item.text}`,
            "radio",
            item.options
          );
          question.condition = {
            questionId: "ITAUD-1",
            value: "Yes"
          };
          questions.push(question);
        }
      });
    }
  });

  return questions;
}

const WORKSPACE_NAME = "Internal Audit";

export default function InternalAuditLanding(_props: InternalAuditLandingProps) {
  const [questions, setQuestions] = useState<TailoringQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { currentEngagement } = useEngagement();
  const [userEngagementRole, setUserEngagementRole] = useState<string | null>(null);
  const [signoffDialogOpen, setSignoffDialogOpen] = useState(false);
  const [cardSignoffs, setCardSignoffs] = useState<MiscSignoffEntry[]>([]);

  const loadCardSignoffs = React.useCallback(async () => {
    try {
      const data = await loadMiscSignoffs();
      setCardSignoffs(getSignoffEntriesForCard(WORKSPACE_NAME, WORKSPACE_NAME, data.signoff));
    } catch {
      setCardSignoffs([]);
    }
  }, []);

  useEffect(() => {
    loadCardSignoffs();
  }, [loadCardSignoffs]);

  useEffect(() => {
    const loadQuestions = async () => {
      try {
        setLoading(true);
        setError(null);

        const jsonData = internalAuditData;
        const transformedQuestions = transformJSONToQuestions(jsonData);
        setQuestions(transformedQuestions);
      } catch (err) {
        console.error("Failed to load questions:", err);
        setError(err instanceof Error ? err.message : "Failed to load questions");
      } finally {
        setLoading(false);
      }
    };

    loadQuestions();
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!currentEngagement || !user) {
        setUserEngagementRole(null);
        return;
      }
      try {
        const team = await apiService.getEngagementTeam(currentEngagement.id);
        const m = team.find((member: any) => String(member.id) === String(user.id));
        setUserEngagementRole(m?.role ?? null);
      } catch {
        setUserEngagementRole(null);
      }
    };
    load();
  }, [currentEngagement, user]);

  if (loading) {
    return (
      <div className="p-6 text-white space-y-6">
        <div>
          <h2 className="text-xl font-semibold mb-1">Internal Audit</h2>
          <p className="text-white/60">Loading questions...</p>
        </div>
        <Card className="border-white/10 bg-white/5">
          <CardContent className="p-6">
            <div className="text-center text-white/60">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4da3ff] mx-auto mb-4"></div>
              <p>Loading questions from JSON file...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-white space-y-6">
        <div>
          <h2 className="text-xl font-semibold mb-1">Internal Audit</h2>
          <p className="text-white/60">Error loading questions</p>
        </div>
        <Card className="border-red-500/20 bg-red-500/5">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 text-red-300">
              <AlertCircle className="h-5 w-5" />
              <p>{error}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="p-6 text-white space-y-6">
        <div>
          <h2 className="text-xl font-semibold mb-1">Internal Audit</h2>
          <p className="text-white/60">No questions found</p>
        </div>
        <Card className="border-white/10 bg-white/5">
          <CardContent className="p-6">
            <div className="text-center text-white/60">
              <FileText className="h-8 w-8 mx-auto mb-4 text-white/40" />
              <p>No questions available. Please check the JSON file.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end items-center gap-2 flex-wrap px-6 pt-2">
        <PlanningSignoffTokens signoffs={cardSignoffs} className="mt-0" />
        <Button variant="outline" size="sm" onClick={() => setSignoffDialogOpen(true)} className="border-white/20 text-white hover:bg-white/10">
          <FileSignature className="h-4 w-4 mr-1.5" />
          Sign off
        </Button>
      </div>
      <PlanningSignoffDialog
        isOpen={signoffDialogOpen}
        onClose={() => setSignoffDialogOpen(false)}
        workspaceName={WORKSPACE_NAME}
        cardNames={[WORKSPACE_NAME]}
        user={user ? { user_name: (user as any).user_name ?? (user as any).name, type: (user as any).type } : null}
        userEngagementRole={userEngagementRole}
        onSignoffDone={loadCardSignoffs}
      />
      <BaseTailoringQuestions
        areaName="ITAUD"
        title="Internal Audit"
        description="Answer the following questions based on internal audit requirements."
        questions={questions}
        onComplete={(answers) => {
          console.log("Internal Audit answers:", answers);
        }}
      />
    </div>
  );
}
